import React from 'react';
import { useTeamChat } from '@/context/TeamChatContext';
import { ArrowLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { format } from 'date-fns';
import MessageComposer from './MessageComposer';
import LinkPreview from './LinkPreview';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';

// Helper to wrap plain URLs in Markdown link syntax
const linkifyText = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  return text.split(urlRegex).map((part) => {
    if (part.match(urlRegex)) {
      if (text.substring(Math.max(0, text.indexOf(part) - 2), text.indexOf(part)).includes('](')) return part;
      return `[${part}](${part})`;
    }
    return part;
  }).join('');
};

const extractUrls = (text: string): string[] => {
  const urls: string[] = [];
  const regex = /https?:\/\/[^\s<]+[^<.,:;"')\]\s]/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    urls.push(match[0]);
  }
  return [...new Set(urls)];
};

interface ThreadPanelProps {
  isGlobalDarkMode: boolean;
  isMobile?: boolean;
  showDetails?: boolean;
}

const ThreadPanel: React.FC<ThreadPanelProps> = ({ isGlobalDarkMode, isMobile = false, showDetails = false }) => {
  const {
    activeThreadId,
    setActiveThread,
    messages,
    getUserById,
    getThreadMessages,
    activeChannelId,
    activeDmId,
  } = useTeamChat();

  if (!activeThreadId) return null;

  const parentMessage = messages.find(m => m.id === activeThreadId);
  const threadMessages = getThreadMessages(activeThreadId);
  const parentUser = parentMessage ? getUserById(parentMessage.userId) : undefined;

  if (!parentMessage) return null;

  const formatMessageTime = (date: Date) => {
    return format(date, 'h:mm a');
  };

  const bgColor = isGlobalDarkMode ? 'bg-background' : 'bg-white';
  const borderColor = isGlobalDarkMode ? 'border-border' : 'border-gray-200';
  const textColor = isGlobalDarkMode ? 'text-foreground' : 'text-gray-900';
  const subTextColor = isGlobalDarkMode ? 'text-muted-foreground' : 'text-gray-500';
  const headerBg = isGlobalDarkMode ? 'bg-background/50' : 'bg-white/50';

  const parentMessageUrls = extractUrls(parentMessage.content).filter(url => !parentMessage.hiddenUrlPreviews?.includes(url));

  return (
    <div className={cn("flex flex-col h-full", isMobile ? "w-full" : "w-full", bgColor)}>
      {/* Header */}
      <div className={cn("h-12 pl-4 flex items-center justify-between border-b backdrop-blur-sm flex-shrink-0 z-10", showDetails ? "pr-4" : "pr-6 md:pr-10", headerBg, borderColor)}>
        <div className="flex items-center gap-2">
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveThread(null)}
              className={cn(
                "p-2 h-8 w-8 flex-shrink-0",
                isGlobalDarkMode
                  ? "hover:bg-card text-muted-foreground hover:text-foreground"
                  : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
              )}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <span className={cn("font-bold text-sm", textColor)}>Thread</span>
        </div>
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-7 w-7", isGlobalDarkMode ? "text-muted-foreground hover:text-foreground hover:bg-card" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100")}
            onClick={() => setActiveThread(null)}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Parent Message */}
      <div className={cn("pl-4 py-4 border-b", showDetails ? "pr-4" : "pr-6 md:pr-10", borderColor)}>
        <div className="flex gap-3">
          <Avatar className="w-9 h-9">
            <AvatarFallback className={cn("text-sm", isGlobalDarkMode ? "bg-card text-muted-foreground" : "bg-primary text-primary-foreground")}>
              {parentUser?.displayName.charAt(0) || '?'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className={cn("font-bold text-sm", textColor)}>{parentUser?.displayName || 'Unknown'}</span>
              <span className={cn("text-[10px]", subTextColor)}>
                {formatMessageTime(new Date(parentMessage.timestamp))}
              </span>
            </div>
            <div className={cn(
              "text-sm mt-1 whitespace-pre-wrap break-words max-w-full sm:max-w-[90%]",
              textColor,
              "[&>p]:mt-0 [&>p]:mb-0 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4",
              isGlobalDarkMode ? "[&>code]:bg-gray-800 [&>code]:text-gray-200 [&>pre]:bg-gray-800/50" : "[&>code]:bg-muted [&>pre]:bg-muted"
            )}>
              <ReactMarkdown
                components={{
                  a: ({ node, ...props }) => (
                    <a 
                      className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      {...props} 
                    />
                  )
                }}
              >{linkifyText(parentMessage.content)}</ReactMarkdown>
            </div>
            {parentMessageUrls.length > 0 && (
              <div className="mt-2 flex flex-col gap-2">
                {parentMessageUrls.map((url, i) => (
                  <LinkPreview key={`thread-parent-${url}-${i}`} url={url} isGlobalDarkMode={isGlobalDarkMode} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Replies Count */}
      {threadMessages.length > 0 && (
        <div className={cn("pl-4 py-2 border-b", showDetails ? "pr-4" : "pr-6 md:pr-10", borderColor)}>
          <span className={cn("text-xs font-semibold uppercase tracking-wider", subTextColor)}>
            {threadMessages.length} {threadMessages.length === 1 ? 'reply' : 'replies'}
          </span>
        </div>
      )}

      {/* Thread Replies - Scrollable */}
      <div className={cn("flex-1 overflow-y-auto pl-4 py-4 space-y-6 min-h-0", showDetails ? "pr-4" : "pr-6 md:pr-10")}>
        {threadMessages.map(message => {
          const user = getUserById(message.userId);
          const messageUrls = extractUrls(message.content).filter(url => !message.hiddenUrlPreviews?.includes(url));
          return (
            <div key={message.id} className="flex gap-3">
              <Avatar className="w-8 h-8">
                <AvatarFallback className={cn("text-xs", isGlobalDarkMode ? "bg-card text-muted-foreground" : "bg-primary text-primary-foreground")}>
                  {user?.displayName.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className={cn("font-bold text-sm", textColor)}>{user?.displayName || 'Unknown'}</span>
                  <span className={cn("text-[10px]", subTextColor)}>
                    {formatMessageTime(new Date(message.timestamp))}
                  </span>
                </div>
                <div className={cn(
                  "text-sm mt-1 whitespace-pre-wrap break-words max-w-full sm:max-w-[90%]",
                  textColor,
                  "[&>p]:mt-0 [&>p]:mb-0 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4",
                  isGlobalDarkMode ? "[&>code]:bg-gray-800 [&>code]:text-gray-200 [&>pre]:bg-gray-800/50" : "[&>code]:bg-muted [&>pre]:bg-muted"
                )}>
                  <ReactMarkdown
                    components={{
                      a: ({ node, ...props }) => (
                        <a 
                          className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          {...props} 
                        />
                      )
                    }}
                  >{linkifyText(message.content)}</ReactMarkdown>
                </div>
                {messageUrls.length > 0 && (
                  <div className="mt-2 flex flex-col gap-2">
                    {messageUrls.map((url, i) => (
                      <LinkPreview key={`thread-msg-${url}-${i}`} url={url} isGlobalDarkMode={isGlobalDarkMode} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Thread Composer - Fixed */}
      <div className="flex-shrink-0">
        <MessageComposer
          channelId={activeChannelId || undefined}
          dmId={activeDmId || undefined}
          threadId={activeThreadId}
          isGlobalDarkMode={isGlobalDarkMode}
          isMobile={isMobile}
          placeholder="Reply..."
          hasRightPanel={showDetails}
        />
      </div>
    </div>
  );
};

export default ThreadPanel;
